// Estimation de la fonction rénale chez l'adulte à partir de la créatininémie.
// CKD-EPI 2009 sans coefficient ethnique est la formule recommandée en France
// (HAS 2011) ; MDRD (version IDMS) reste imprimée par certains laboratoires ;
// Cockcroft-Gault sert à l'adaptation des posologies dans les RCP.

export const UNITS = {
  umol: { label: "µmol/L", toMgDl: (v) => v / 88.4 },
  mgl: { label: "mg/L", toMgDl: (v) => v / 10 },
  mgdl: { label: "mg/dL", toMgDl: (v) => v },
};

export const toMgDl = (value, unit) => UNITS[unit].toMgDl(value);

// mL/min/1,73 m². scr en mg/dL. Constantes du tableau de la publication
// d'origine (Levey 2009) : 144 chez la femme, 141 chez l'homme. La forme
// condensée « 141 × 1,018 » donne 143,5 et décale certains arrondis d'une unité
// par rapport au calculateur de la SFNDT, qui utilise 144.
export function ckdEpi2009({ age, sex, scr }) {
  const female = sex === "female";
  const k = female ? 0.7 : 0.9;
  const a = female ? -0.329 : -0.411;
  const r = scr / k;
  return (female ? 144 : 141) * Math.min(r, 1) ** a * Math.max(r, 1) ** -1.209 * 0.993 ** age;
}

// mL/min/1,73 m². scr en mg/dL (créatinine standardisée IDMS).
export function mdrd({ age, sex, scr }) {
  return 175 * scr ** -1.154 * age ** -0.203 * (sex === "female" ? 0.742 : 1);
}

// mL/min, non indexé. Forme d'origine (Cockcroft et Gault, 1976) :
// (140 − âge) × poids ÷ (72 × créatininémie en mg/dL), × 0,85 chez la femme.
// Les constantes « 1,23 / 1,04 » souvent citées en µmol/L en sont des arrondis
// (88,4 ÷ 72 = 1,228 ; × 0,85 = 1,044).
export function cockcroftGault({ age, sex, weight, scr }) {
  return (((140 - age) * weight) / (72 * scr)) * (sex === "female" ? 0.85 : 1);
}

// Stades de la maladie rénale chronique selon le DFG (KDIGO, repris par la HAS).
// G1 et G2 ne définissent une maladie rénale chronique qu'en présence d'autres
// marqueurs d'atteinte rénale persistant plus de 3 mois.
export const STAGES = [
  { min: 90, code: "G1", label: "DFG normal ou élevé" },
  { min: 60, code: "G2", label: "DFG légèrement diminué" },
  { min: 45, code: "G3a", label: "Diminution légère à modérée" },
  { min: 30, code: "G3b", label: "Diminution modérée à sévère" },
  { min: 15, code: "G4", label: "Diminution sévère" },
  { min: 0, code: "G5", label: "Insuffisance rénale terminale" },
];

export const stageFor = (dfg) => STAGES.find((s) => dfg >= s.min);

// Adultes uniquement : ces équations ne s'appliquent pas à l'enfant. Les bornes
// de créatinine écartent les erreurs d'unité les plus grossières (µmol/L saisi
// en mg/dL, par exemple).
export function validate({ age, scr, weight }) {
  return {
    age: age >= 18 && age <= 110,
    scr: scr >= 0.2 && scr <= 25,
    weight: weight >= 30 && weight <= 250,
  };
}
